import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { arClass } from "../../utils/useArClass";
import Txt from "../../components/Text";

import CreateSubscriptionRequest from "../../components/private/request/forms/CreateSubscriptionRequest";
import CreateModelRequest from "../../components/private/request/forms/CreateModelRequest";
import CreateRoleRequest from "../../components/private/request/forms/CreateRoleRequest";
import CreateUserAccessRequest from "../../components/private/request/forms/CreateUserAccessRequest";
import CreateHelpRequest from "../../components/private/request/forms/CreateHelpRequest";
import { CreditCard, Key, Package, ShieldCheck, UserCog, HelpCircle, ArrowLeft } from "lucide-react";

const Text = ({ children, className, page = "MY_REQUESTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const CreateRequest = () => {
  const navigate = useNavigate();
  const darkMode = useSelector((state) => state.settings.darkMode);
  
  const {requestType,userId} = useParams();
  const [selectedType, setSelectedType] = useState( null || requestType);


  

  // If URL param is valid, set it as the selected type
  const requestTypes = [
    {
      type: "SUBSCRIPTION_REQUEST",
      label: "Subscription Change",
      description: "Upgrade, downgrade, or change subscription plans",
      icon: <CreditCard />,
      color: "from-green-500 to-green-600",
    },
    {
      type: "MODEL_REQUEST",
      label: "Model Request",
      description: "Request new ML models or data structures",
      icon: <Package />,
      color: "from-purple-500 to-purple-600",
    },
    {
      type: "ROLE_REQUEST",
      label: "Role Request",
      description: "Request role changes or promotions",
      icon: <UserCog />,
      color: "from-orange-500 to-orange-600",
    },
    {
      type: "USER_ACCESS_REQUEST",
      label: "User Access Request",
      description: "Manage user access and permissions",
      icon: <Key />,
      color: "from-red-500 to-red-600",
    },
    {
      type: "HELP_REQUEST",
      label: "Help Request",
      description: "Get help from our support team",
      icon: <HelpCircle />,
      color: "from-amber-500 to-orange-600",
    },
  ];

  const handleCancel = () => {
    setSelectedType(null);
    navigate("/create-request");
  };

  const renderForm = () => {
    switch (selectedType) {
      case "SUBSCRIPTION_REQUEST":
        return <CreateSubscriptionRequest onCancel={handleCancel} />;
      case "MODEL_REQUEST":
        return <CreateModelRequest onCancel={handleCancel} />;
      case "ROLE_REQUEST":
        return <CreateRoleRequest onCancel={handleCancel} />;
      case "USER_ACCESS_REQUEST":
        return <CreateUserAccessRequest onCancel={handleCancel} />;
      case "HELP_REQUEST":
        return <CreateHelpRequest onCancel={handleCancel} />;
      default:
        return null;
    }
  };

  if (selectedType) {
    return renderForm();
  }

  return (
    <div
      className={arClass(`min-h-screen ${
        darkMode ? "bg-gray-900" : "bg-gray-50"
      } transition-colors duration-300`)}
    >
      <div className={arClass(" mx-auto px-4 py-8 sm:px-6 lg:px-8")}>
        {/* Back Button */}
        <motion.button
          onClick={() => navigate("/my-requests")}
          className={arClass(
            `flex items-center gap-2 px-4 py-2 rounded-lg font-medium mb-8 ${
              darkMode
                ? "bg-gray-800 hover:bg-gray-700 text-white"
                : "bg-white hover:bg-gray-100 text-gray-900"
            }`
          )}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft className="w-4 h-4" />
          <Text>Back</Text>
        </motion.button>

        {/* Header */}
        <div className={arClass("mb-8")}>
          <h1
            className={arClass(
              `text-2xl sm:text-3xl font-bold ${
                darkMode ? "text-white" : "text-gray-900"
              }`
            )}
          >
            <Text>Create New Request</Text>
          </h1>
          <p
            className={arClass(
              `mt-2 text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`
            )}
          >
            <Text>Select the type of request you want to create</Text>
          </p>
        </div>

        {/* Request Type Grid */}
        <div className={arClass("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6")}>
          {requestTypes.map((reqType) => (
            <motion.button
              key={reqType.type}
              onClick={() => setSelectedType(reqType.type)}
              className={arClass(
                `flex flex-col items-start p-6 rounded-lg border-2 transition-all ${
                  darkMode
                    ? "bg-gray-800 border-gray-700 hover:border-gray-600"
                    : "bg-white border-gray-200 hover:border-gray-300"
                }`
              )}
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={arClass("mb-4 text-4xl")}>{reqType.icon}</div>
              <h3
                className={arClass(
                  `text-lg font-semibold mb-2 max-sm:text-sm ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`
                )}
              >
                <Text>{reqType.label}</Text>
              </h3>
              <p
                className={arClass(
                  `text-sm max-sm:text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`
                )}
              >
                <Text>{reqType.description}</Text>
              </p>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CreateRequest;
